import { FlowPageInterface, ProspectionServiceResource } from "@/interfaces";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-pad-wrapper";
import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { CircleCheck, Download, Loader2 } from "lucide-react";
import useFormRequest from "@/hooks/useFormRequest";
import { useTranslation } from "react-i18next";
import { AcceptContractForm } from "@/types/contract";
import { useAttachProspectionServicesMutation, useContract, useProspectionServices } from "@/hooks/useContract";
import { offerablePriceModes, offerableProspectionServices } from "@/utils/prospection-offerability";
import { ContractBlocker, getContractBlocker } from "@/utils/contract-blockers";
import { apiRefusalDetail } from "@/utils/api-refusal";
import { cn } from "@/lib/utils";
import RadioDot from "./payment/RadioDot";
import { formatCurrency } from "@/utils/currency";
import { Skeleton } from "./ui/skeleton";
import {ContactUs} from "@/components/ContactUs.tsx";
import { usePaymentPlansAvailable } from "@/hooks/payments/usePaymentPlansAvailable";
import InvoiceSkeleton from "@/components/invoice-skeleton";
import ContractAvailableDiscounts from "@/components/contract-available-discounts";
import { describePriceMode } from "@/utils/price-mode";
import ClausesAcknowledgmentDialog from "@/app/clauses/components/clauses-acknowledgment-dialog";
import { useAvailableClausesService } from "@/app/clauses/services/use-available-clauses-service";
import ContractAppendices from "@/app/appendices/components/contract-appendices";
import ContractPaperFrame from "@/components/contract-paper-frame";
import { loadTypedSignatureFont, paintTypedSignature } from "@/utils/typed-signature";

const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024;
const CONTRACT_STATUS_ACTIVE = 1;
const CLEAR_SIGNATURE_CLASS_NAME =
  "absolute top-3 right-3 text-xs text-foreground bg-transparent cursor-pointer hover:underline underline-offset-2";

type SignatureMode = "draw" | "type";

/**
 * Keyed rather than a ternary: every refusal has to name its own copy, so adding one
 * fails the build instead of silently telling the customer their contractor set up no
 * payment options.
 */
const REFUSAL_COPY_KEYS: Record<Exclude<ContractBlocker, null | "deciding">, string> = {
  "no-services": "fe.pages.flow.steps.contract.noServices",
  "no-priced-services": "fe.pages.flow.steps.contract.noPricedServices",
  "no-payment-plans": "fe.pages.flow.steps.contract.noPaymentPlans",
};

export const FlowPageContract = (props: FlowPageInterface) => {
  const { t } = useTranslation();

  const { contractId } = useFlow();
  const { 
    data: contract, 
    refetch: refetchContract,
    isFetching: isFetchingContract
  } = useContract(contractId);

  const { 
    data: prospectionServices, 
    isLoading: isLoadingServices,
    isFetching: isFetchingServices,
    isError: servicesFailed
  } = useProspectionServices(contractId);

  const { 
    mutateAsync: attachService, 
    isPending: isAttaching 
  } = useAttachProspectionServicesMutation();

  const { data: paymentPlans, isFetching: isFetchingPaymentPlans } =
    usePaymentPlansAvailable(contractId);

  const { data: clauses, isLoading: isLoadingClauses } = useAvailableClausesService(contractId);
  const pendingClauses = (clauses ?? []).filter((clause) => !clause.attributes.accepted_at);

  const contractSignatureUrl = contract?.attributes.signature_url;
  // The contract is locked once it was signed in the portal, activated in the
  // back office, or has its acceptance flags set — in all those cases the user
  // may re-read it but must not sign again.
  const isContractLocked = Boolean(
    contract?.attributes.signature_url ||
    Number(contract?.attributes.status_id) === CONTRACT_STATUS_ACTIVE ||
    (contract?.attributes.is_terms_accepted && contract?.attributes.is_clauses_accepted)
  );
  const isContractProspected = contract?.meta?.is_prospection;
  const company = contract?.relationships?.company?.data;
  /**
   * An option the company has not priced for this address cannot be sold: the backend refuses to
   * attach it, so offering it can only end in a dead end. `has_pricing` does not answer this —
   * for a size-based product it is true whenever *any* tier row exists, whatever this contract's
   * address resolves to — so the price itself is the test.
   */
  const offerableServices = offerableProspectionServices(prospectionServices);
  const hasProspectionServices = !!offerableServices.length;
  /**
   * Whether the step renders at all, and why not. Ordered in one place so a refusal is never
   * shown — or the step painted — before the requests that decide it have answered.
   */
  const blocker = getContractBlocker({
    isProspection: !!isContractProspected,
    isLocked: isContractLocked,
    isFetchingServices,
    isFetchingContract,
    servicesFailed,
    hasLoadedServices: prospectionServices !== undefined,
    hasOfferableService: hasProspectionServices,
    // Only an explicit false: until the contract has loaded, nothing is known to be missing.
    hasNoServices: contract?.meta?.has_services === false,
    isCardCaptureOnly: Boolean(contract?.meta?.card_capture_only),
    isFetchingPaymentPlans,
    hasNoPaymentPlans: paymentPlans !== undefined && paymentPlans.length === 0,
  });

  /**
   * The server has the final say on whether an option can be sold, and it refuses with a message
   * meant for the customer. Without this the rejected promise was swallowed: the Accept button
   * simply re-enabled and nothing happened, which is the symptom this ticket exists to remove.
   */
  const [attachError, setAttachError] = useState<string | null>(null);
  /**
   * The same goes for the signature itself: the API can still refuse it (no services, a clause
   * left, a closed season) after the step was drawn from an older copy of the contract.
   */
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const [haveSignature, setHaveSignature] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [clausesAccepted, setClausesAccepted] = useState<boolean>(false);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  /**
   * The pad is not in the tree on every render of this step — a blocker can hold the step, and a
   * locked contract renders its signature instead — so its setup has to key on the pad itself.
   * Keyed on `currentlyVisible` alone the effect fired against a null ref and never ran again,
   * leaving the pad with no stroke listeners: signing did nothing and Accept stayed disabled.
   */
  const [signaturePadInstance, setSignaturePadInstance] = useState<SignaturePad | null>(null);
  const attachSignaturePad = useCallback((wrapper: SignaturePad | null) => {
    signaturePadRef.current = wrapper;
    setSignaturePadInstance(wrapper);
  }, []);
  const placeholderActiveRef = useRef(true);
  const [signatureMode, setSignatureMode] = useState<SignatureMode>("draw");
  const [typedSignature, setTypedSignature] = useState<string>("");
  const typedSignatureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Signature prepared but held back while the clause gate is open: it is sent
  // only once every clause has been accepted.
  const [isClauseGateOpen, setIsClauseGateOpen] = useState<boolean>(false);
  const heldSignatureRef = useRef<File | null>(null);

  const [selectedService, setSelectedService] = useState<number | null>(null);
  // The chosen pricing option for the selected service. Null for legacy/modeless
  // services (backend then falls back to the committed price).
  const [selectedPriceMode, setSelectedPriceMode] = useState<number | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState<boolean>(false);
  const [documentHeight, setDocumentHeight] = useState<number>();
  const contractFrameRef = useRef<HTMLIFrameElement | null>(null);

  const { loading, send, form, setValue } = useFormRequest<AcceptContractForm, any>(
    `/folloportal/contracts/${contractId}/accept`,
    "POST",
    {
      meta: {
        is_terms_accepted: false,
        is_clauses_accepted: false,
        signature: null,
      },
    },
   () => {
      if (contractId) {
        refetchContract();
      }
      props.nextStep();
    },
    (error) => {
      const reason = apiRefusalDetail(error);
      setAcceptError(reason);
      // The contract the step was drawn from is out of date; the fresh one may carry its own refusal.
      if (reason && contractId) {
        refetchContract();
      }
    },
    {
      isMultipart: true,
      transform: (data) => {
        const fd = new FormData();
        fd.append("meta[is_terms_accepted]", data.meta.is_terms_accepted ? "1" : "0");
        fd.append("meta[is_clauses_accepted]", data.meta.is_clauses_accepted ? "1" : "0");
        if (data.meta.signature) {
          fd.append("meta[signature]", data.meta.signature);
        }
        return fd;
      },
    }
  );

  const dataURLToFile = async (dataUrl: string, filename: string): Promise<File> => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  };

  const drawPlaceholder = (signaturePad: SignaturePad) => {
    const canvas = signaturePad.canvas.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const padding = 20;
    const textY = canvas.height - padding;

    ctx.save();
    ctx.font = "12px Inter, system-ui, sans-serif";
    ctx.fillStyle = "#D4D4D8";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText(t("models.contract.placeHolderSignature"), padding, textY);
    ctx.restore();

    placeholderActiveRef.current = true;
  };

  const handleResetSignature = () => {
    const wrapper = signaturePadRef.current;
    const signaturePad = wrapper?.instance;

    if (!wrapper || !signaturePad) return;

    signaturePad.clear();
    setHaveSignature(false);

    placeholderActiveRef.current = true;

    setValue("meta.signature", null, { shouldValidate: true });
    form.clearErrors("meta.signature");

    drawPlaceholder(wrapper);
  }

  const drawnSignatureDataUrl = (): string | null => {
    const signaturePad = signaturePadRef.current?.instance;
    if (!signaturePad || signaturePad.isEmpty()) return null;

    return signaturePad.toDataURL("image/png");
  };

  const typedSignatureDataUrl = async (): Promise<string | null> => {
    const canvas = typedSignatureCanvasRef.current;
    const name = typedSignature.trim();
    if (!canvas || !name) return null;

    await loadTypedSignatureFont();
    paintTypedSignature(canvas, name);

    return canvas.toDataURL("image/png");
  };

  /** Validated signature file, or null when the pad content is not usable. */
  const prepareSignature = async (): Promise<File | null> => {
    const dataUrl = signatureMode === "type" ? await typedSignatureDataUrl() : drawnSignatureDataUrl();

    // Validate signature
    if (!dataUrl) {
      form.setError("meta.signature", {
        type: "manual",
        message: "La <strong>signature</strong> est obligatoire"
      });
      return null;
    }

    const signatureFile = await dataURLToFile(dataUrl, "signature.png");

    if (signatureFile.size > MAX_SIGNATURE_BYTES) {
      form.setError("meta.signature", {
        type: "manual",
        message: "La <strong>signature</strong> ne doit pas dépasser 2 Mo.",
      });
      return null;
    }

    return signatureFile;
  };

  /**
   * Attaches the chosen option, or reports why it was refused and stops. Returns false when the
   * caller must not carry on — the contract has to keep its unsigned state if the line it would
   * be priced from could not be created.
   */
  const attachSelectedService = async (): Promise<boolean> => {
    if (!isContractProspected) {
      return true;
    }

    // A prospected quote is priced by the line this attaches, so proceeding without one would
    // activate it at $0. That includes the case where the list never arrived: not knowing what is
    // sellable is not permission to sell nothing.
    if (!hasProspectionServices || !selectedService) {
      setAttachError(t("fe.pages.flow.steps.contract.serviceCouldNotBeAttached"));

      return false;
    }

    setAttachError(null);

    try {
      await attachService({
        contractId: contractId!,
        serviceId: Number(selectedService),
        priceModeId: selectedPriceMode,
      });

      return true;
    } catch (error) {
      const response = (error as {
        response?: { status?: number; data?: { message?: string } };
      }).response;

      // Only a 422 carries wording written for the customer. Anything else is an internal
      // failure, whose message would be a server or driver string.
      setAttachError(
        (response?.status === 422 ? response?.data?.message : null) ??
          t("fe.pages.flow.steps.contract.serviceCouldNotBeAttached")
      );

      return false;
    }
  };

  const submitContract = async (signatureFile: File): Promise<boolean> => {
    if (!(await attachSelectedService())) {
      return false;
    }

    setValue("meta.is_terms_accepted", termsAccepted);
    setValue("meta.is_clauses_accepted", clausesAccepted);
    setValue("meta.signature", signatureFile);

    setAcceptError(null);
    await send();

    return true;
  };

  /**
   * An already-accepted contract shows a plain "next" instead of the signing flow,
   * so the prospection choice has to be attached here too — otherwise the customer
   * picks an option, the step advances, and the contract stays priced at zero.
   */
  const handleContinueAccepted = async () => {
    if (!(await attachSelectedService())) {
      return;
    }

    props.nextStep();
  };

  /**
   * Clauses have to be acknowledged before the contract is signed, so a
   * contract with pending clauses opens the gate instead of sending the
   * signature: the request resumes in handleAllClausesAccepted.
   */
  const handleAcceptContract = async () => {
    const signatureFile = await prepareSignature();
    if (!signatureFile) return;

    if (pendingClauses.length > 0) {
      heldSignatureRef.current = signatureFile;
      setIsClauseGateOpen(true);

      return;
    }

    await submitContract(signatureFile);
  };

  /**
   * Backing out of the gate: nothing was accepted, so the signature stays held
   * and the contract is not sent. The drawing survives, so asking to sign again
   * reopens the gate rather than making the client sign a second time.
   */
  const handleClauseGateDismissed = () => setIsClauseGateOpen(false);

  const handleAllClausesAccepted = async () => {
    setIsClauseGateOpen(false);

    const signatureFile = heldSignatureRef.current;

    if (signatureFile) {
      // Cleared only once the submit went through: a refused attach used to throw the signature
      // away, leaving the customer to draw it again for a quote they had already signed.
      if (await submitContract(signatureFile)) {
        heldSignatureRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!isContractLocked) return;
    setHaveSignature(true);
    setTermsAccepted(true);
    setClausesAccepted(true);
  }, [isContractLocked]);

  useEffect(() => {
    const wrapper = signaturePadInstance;
    const signaturePad = wrapper?.instance;
    if (!signaturePad) return;

    // Draw initial placeholder
    handleResetSignature();

    const handleBegin = () => {
      if (!placeholderActiveRef.current) return;
      const canvas = wrapper.canvas.current;

      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      placeholderActiveRef.current = false;
    };

    const handleEnd = () => {
      const signed = !signaturePad.isEmpty();
      setHaveSignature(signed);
      if (signed) {
        form.clearErrors("meta.signature");
        placeholderActiveRef.current = false;
      }
    };

    signaturePad.addEventListener("beginStroke", handleBegin);
    signaturePad.addEventListener("endStroke", handleEnd);

    // The canvas backing store is sized once on mount, so it has to be
    // rescaled whenever the rendered width changes — otherwise every stroke is
    // drawn at an offset. `resize` on window is not enough: the column width
    // also changes with the surrounding layout, not just with the viewport.
    const canvas = wrapper.canvas.current;
    const observer = new ResizeObserver(() => {
      wrapper.handleResize();
      if (signaturePad.isEmpty()) drawPlaceholder(wrapper);
    });
    if (canvas) observer.observe(canvas);

    return () => {
      observer.disconnect();
      signaturePad.removeEventListener("beginStroke", handleBegin);
      signaturePad.removeEventListener("endStroke", handleEnd);
    };
  }, [signaturePadInstance, props.currentlyVisible, contractId]);

  useEffect(() => {
    const canvas = typedSignatureCanvasRef.current;
    if (!canvas || signatureMode !== "type") return;

    let cancelled = false;
    const paint = () => paintTypedSignature(canvas, typedSignature.trim());

    paint();
    loadTypedSignatureFont().then(() => {
      if (!cancelled) paint();
    });

    const observer = new ResizeObserver(paint);
    observer.observe(canvas);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [signatureMode, typedSignature]);

  const handleSignatureModeChange = (mode: string) => {
    setSignatureMode(mode as SignatureMode);
    form.clearErrors("meta.signature");
  };

  const handleTypedSignatureChange = (name: string) => {
    setTypedSignature(name);
    if (name.trim()) form.clearErrors("meta.signature");
  };

  // The document is cross-origin, so its height can only come from the document
  // itself. Until it reports, the frame falls back to its paper-size estimate.
  useEffect(() => {
    const previewUrl = contract?.attributes.link_to_html_pdf;
    if (!previewUrl) return;

    // Relative would throw; a base keeps the feature working instead of silently dying.
    const previewOrigin = new URL(previewUrl, window.location.href).origin;

    const onMessage = (event: MessageEvent) => {
      // That host serves every tenant's previews, so the origin alone is not enough —
      // only the frame we embedded may size it.
      if (event.origin !== previewOrigin) return;
      if (event.source !== contractFrameRef.current?.contentWindow) return;
      if (event.data?.type !== "setHeight") return;

      setDocumentHeight(Number(event.data.height));
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [contract?.attributes.link_to_html_pdf]);

  const handleDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const signatureError = (form.formState.errors as any)?.meta?.signature?.message;
  const termsError = (form.formState.errors as any)?.meta?.is_terms_accepted?.message;
  const clausesError = (form.formState.errors as any)?.meta?.is_clauses_accepted?.message;

  const isSigned = signatureMode === "type" ? typedSignature.trim() !== "" : haveSignature;
  const needsServiceSelection = isContractProspected && hasProspectionServices && !selectedService;
  const canAcceptContract = isSigned && termsAccepted && clausesAccepted && !isContractLocked && !needsServiceSelection && !isLoadingClauses;

  const isSubmitting = loading || isAttaching || isClauseGateOpen;

  if (!props.currentlyVisible) {
    return null;
  }

  /**
   * The hold is rendered inside the step rather than in place of it. Replacing the step
   * takes the signature panel down with it, and the pad's canvas has to be laid out to
   * size itself — so the column that owns it stays mounted, and only the side that a
   * refusal would speak for is held.
   */
  const isDeciding = blocker === "deciding";

  if (blocker && !isDeciding) {
    return (
      <>
        <div className="follo-card">
          <div className="flex items-center justify-center min-h-[400px] text-sm text-muted-foreground px-7 py-10 text-center">
            <span className="max-w-xl">{t(REFUSAL_COPY_KEYS[blocker])}</span>
          </div>
        </div>
        <ContactUs email={company?.attributes.email} />
      </>
    );
  }

  return (
    <div className="grid md:grid-cols-3 grid-cols-1 gap-9">
      {/* min-w-0: the contract paper is laid out at full letter width and a
          grid item defaults to min-width:auto, which would let it stretch this
          column past the viewport. */}
      <div className="col-span-1 md:col-span-2 min-w-0">
        {isDeciding ? (
          <div className="follo-card">
            <div className="flex items-center justify-center min-h-[400px] px-7 py-10">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          </div>
        ) : (
        <div className="follo-card overflow-hidden">
          {isContractProspected && (
            <div className="px-7 py-6 flex flex-col gap-3">
              <h3 className="follo-label">
                {t("fe.pages.flow.steps.contract.service-title")}
              </h3>
              <p className="text-sm text-muted-foreground -mt-1">
                {t("fe.pages.flow.steps.contract.service-subtitle")}
              </p>
              {isLoadingServices ? (
                <>
                  <SkeletonServiceCard />
                  <SkeletonServiceCard />
                </>
              ) : hasProspectionServices ? (
                <>
                  {offerableServices.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      selectedServiceId={selectedService}
                      selectedPriceModeId={selectedPriceMode}
                      onSelect={(serviceId, priceModeId) => {
                        setAttachError(null);
                        setSelectedService(serviceId);
                        setSelectedPriceMode(priceModeId);
                      }}
                    />
                  ))}
                </>
              ) : null}
            </div>
          )}
          <ContractAvailableDiscounts paymentPlans={paymentPlans} />
          <div className="px-7 py-6 flex items-center justify-between">
            <h3 className="follo-label">
              {t("fe.pages.flow.steps.contract.title")}
            </h3>
            <Button
              className="cursor-pointer" 
              variant="outline" 
              onClick={() => handleDownload(contract?.attributes.link_to_pdf || "")}
              >
              <Download size={16} className="mr-1.5" />
              {t("fe.pages.flow.steps.contract.download")}
            </Button>
          </div>
          {needsServiceSelection ? (
            <div className="flex items-center justify-center min-h-[400px] bg-background text-base font-medium text-muted-foreground px-7 py-10 text-center">
              {t("fe.pages.flow.steps.contract.service-description")}
            </div>
          ) : (
            <>
              <ContractPaperFrame className="relative" documentHeight={documentHeight}>
                <iframe
                  className={cn(
                    "w-full h-full block border-0",
                    !iframeLoaded && "opacity-0"
                  )}
                  ref={contractFrameRef}
                  src={contract?.attributes.link_to_html_pdf}
                  title="Contract"
                  onLoad={(e) => {
                    const target = e.currentTarget;
                    if (target.src === contract?.attributes.link_to_html_pdf) {
                      setIframeLoaded(true);
                    }
                  }}
                />
                {!iframeLoaded && (
                  <InvoiceSkeleton className="absolute inset-0 z-10" />
                )}
              </ContractPaperFrame>
              <ContractAppendices contractId={contractId} />
            </>
          )}
        </div>
        )}
        <ContactUs email={company?.attributes.email} />
      </div>
      {/* The contract frame is now as tall as the document, so without this the sign-and-accept
          panel sits at the top of a very long grid row and scrolls out of reach. */}
      <div className="col-span-1 md:sticky md:top-4 md:self-start">
        <div className="follo-card p-7 flex flex-col gap-6">
          <h3 className="follo-label">
            {t("fe.pages.flow.steps.contract.title2")}
            <span className="text-destructive ml-1">*</span>
          </h3>
          {!isContractLocked ? (
            <div className="flex flex-col gap-3">
              <Tabs value={signatureMode} onValueChange={handleSignatureModeChange}>
                <TabsList>
                  <TabsTrigger value="draw">{t("models.contract.drawSignature")}</TabsTrigger>
                  <TabsTrigger value="type">{t("models.contract.typeSignature")}</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative">
                <div className={cn("relative", signatureMode === "type" && "invisible absolute inset-x-0 top-0")}>
                  <SignaturePad
                    ref={attachSignaturePad}
                    height={120}
                    redrawOnResize
                    // The explicit CSS height matters: the wrapper only sets the
                    // canvas *attribute* height, and with width:100% the canvas
                    // would otherwise scale to its intrinsic ratio, so the drawn
                    // stroke lands offset from the pointer.
                    canvasProps={{ className: "signature-pad block w-full h-[120px]" }}
                    options={{ backgroundColor: "#F4F4F5" }}
                  />
                  {haveSignature && (
                    <button onClick={handleResetSignature} className={CLEAR_SIGNATURE_CLASS_NAME}>
                      {t("models.contract.clearSignature")}
                    </button>
                  )}
                </div>
                {signatureMode === "type" && (
                  <div className="flex flex-col gap-3">
                    <Input
                      autoComplete="name"
                      placeholder={t("models.contract.typedSignaturePlaceholder")}
                      value={typedSignature}
                      onChange={(event) => handleTypedSignatureChange(event.target.value)}
                    />
                    <div className="relative">
                      <canvas ref={typedSignatureCanvasRef} className="block w-full h-[120px] rounded-lg bg-[#F4F4F5]" />
                      {typedSignature.trim() && (
                        <button onClick={() => handleTypedSignatureChange("")} className={CLEAR_SIGNATURE_CLASS_NAME}>
                          {t("models.contract.clearSignature")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {signatureError && <p className="text-sm text-destructive" dangerouslySetInnerHTML={{ __html: String(signatureError) }} />}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {contractSignatureUrl && (
                <img
                  className="w-full object-contain max-h-[120px]"
                  src={contractSignatureUrl}
                  alt="signature"
                />
              )}
              <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                <CircleCheck size={16} />
                {t("fe.pages.flow.steps.contract.alreadySigned")}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Checkbox
              disabled={isContractLocked}
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(!!checked)}
            />
            <Label htmlFor="terms" className="block leading-5">
              {t("models.contract.contractDeclarationTarget")}
              <span className="text-destructive ml-1">*</span>
              {termsError && <p className="text-sm text-destructive mt-1" dangerouslySetInnerHTML={{ __html: String(termsError) }} />}
            </Label>
          </div>
          <div className="flex gap-2">
            <Checkbox
              disabled={isContractLocked}
              id="clauses"
              checked={clausesAccepted}
              onCheckedChange={(checked) => setClausesAccepted(!!checked)}
            />
            <Label htmlFor="clauses" className="block leading-5">
              {t("models.contract.contractAcknowledgement")}
              <span className="text-destructive ml-1">*</span>
              {clausesError && <p className="text-sm text-destructive mt-1" dangerouslySetInnerHTML={{ __html: String(clausesError) }} />}
            </Label>
          </div>
          {(attachError ?? acceptError) && (
            <p className="text-sm text-destructive text-right mb-2" role="alert">
              {attachError ?? acceptError}
            </p>
          )}
          <div className="text-right">
            {!isContractLocked ?  (
              <Button
                className="cursor-pointer"
                variant="success"
                size="lg"
                disabled={!canAcceptContract || isSubmitting}
                onClick={handleAcceptContract}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    {t("ui.loading")}
                  </>
                ) : (
                  t("ui.acceptContract")
                )}
              </Button>
              ) : (
                <Button
                  className="cursor-pointer"
                  disabled={needsServiceSelection || isAttaching}
                  onClick={handleContinueAccepted}
                >
                  {t("ui.next")}
                </Button>
              )
            }
          </div>
        </div>
      </div>

      {/* Holds the signature request until every clause is acknowledged, so
          each clause acceptance is recorded before the contract is signed. */}
      <ClausesAcknowledgmentDialog
        contractId={contractId}
        open={isClauseGateOpen}
        onAllAccepted={handleAllClausesAccepted}
        onDismiss={handleClauseGateDismissed}
      />
    </div>
  );
};

type ServiceCardProps = {
  service: ProspectionServiceResource;
  selectedServiceId: number | null;
  selectedPriceModeId: number | null;
  onSelect: (serviceId: number, priceModeId: number | null) => void;
  disabled?: boolean;
};

function ServiceCard({ service, selectedServiceId, selectedPriceModeId, onSelect, disabled }: ServiceCardProps) {
  const { t } = useTranslation();
  const { name, service_type, size_tier, taxable, promotion } = service.attributes;
  // A $0 row on an otherwise priced service is not sellable either — the server refuses it, so
  // showing it would put a "$0.00" option in front of the customer that cannot be accepted.
  const price_modes = offerablePriceModes(service);
  const heading = service_type ? `${name} (${service_type})` : name;

  const taxLabel = taxable ? t("fe.pages.flow.steps.contract.taxable") : "";

  return (
    <div className={cn("w-full border rounded-xl overflow-hidden", disabled ? "opacity-60" : "")}>
      <div className="px-4 py-3 bg-muted/40 flex justify-between items-center gap-3">
        <div className="flex flex-col">
          <span className="text-base font-medium text-primary">{heading}</span>
          {size_tier && <span className="text-xs text-muted-foreground">{size_tier}</span>}
        </div>
        {price_modes.length > 0 && (
          <span className="text-xs font-semibold uppercase tracking-wide text-blue-600 whitespace-nowrap">
            {t("fe.pages.flow.steps.contract.choose-pricing-option")}
          </span>
        )}
      </div>

      {promotion && (
        <div className="px-4 py-2 bg-green-50 border-t border-green-200 text-xs text-green-800">
          <span className="font-semibold">{promotion.name}</span>
          {" — "}
          {t("fe.pages.flow.steps.contract.promotion", {
            deadline: promotion.deadline,
            discounted: formatCurrency(promotion.price_total_with_discount),
            full: formatCurrency(service.attributes.price_total),
          })}
        </div>
      )}

      {price_modes.length > 0 ? (
        price_modes.map((mode) => {
          const checked = selectedServiceId === service.id && selectedPriceModeId === mode.id;
          const description = describePriceMode(mode, t);

          return (
            <PricingOptionRow
              key={mode.id}
              checked={checked}
              description={description}
              disabled={disabled}
              label={mode.name}
              price={mode.price_total}
              taxLabel={taxLabel}
              onClick={() => onSelect(service.id, mode.id)}
            />
          );
        })
      ) : (
        <PricingOptionRow
          checked={selectedServiceId === service.id}
          disabled={disabled}
          label={name}
          price={service.attributes.price_total}
          taxLabel={taxLabel}
          onClick={() => onSelect(service.id, null)}
        />
      )}
    </div>
  );
}

type PricingOptionRowProps = {
  checked: boolean;
  label: string;
  price: number;
  taxLabel: string;
  onClick: () => void;
  description?: string;
  disabled?: boolean;
};

function PricingOptionRow({ checked, label, price, taxLabel, onClick, description, disabled }: PricingOptionRowProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-between px-4 py-3 border-t cursor-pointer transition-all text-left",
        checked ? "bg-muted" : "",
        disabled ? "cursor-not-allowed" : ""
      )}
    >
      <div className="flex items-center gap-3">
        <RadioDot checked={checked} />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-primary">{label}</span>
          {description && <span className="text-xs text-muted-foreground">{description}</span>}
        </div>
      </div>
      <div className="flex items-baseline gap-1.5 flex-shrink-0 ml-4">
        <span className="text-lg font-bold text-primary">{formatCurrency(price)}</span>
        <span className="text-xs text-muted-foreground">{taxLabel}</span>
      </div>
    </button>
  );
}

function SkeletonServiceCard() {
  return (
    <div className="w-full h-16 flex items-center justify-between px-4 border rounded-xl">
      <div className="flex items-center gap-3">
        <Skeleton className="w-5 h-5 rounded-full" />
        <Skeleton className="h-4 w-40" />
      </div>
      <Skeleton className="h-5 w-24" />
    </div>
  );
}